/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import type { ComponentType } from '@/components/region';
import { type RegionItem, NO_REGION_ITEMS, useRegionItems, withComponentData } from '@/components/region-items';
import { SectionHeader } from '@/components/section-header';
import { Carousel as EmblaCarousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { cn } from '@/lib/utils';

const carouselDefaults = {
    itemsMobile: 1,
    itemsTablet: 1,
    itemsDesktop: 1,
    showHalfItem: false,
    showHalfItemMobile: false,
    dotsPosition: 'over',
    disableAutoPlay: false,
    autoPlayInterval: 5000,
    disableLoop: false,
    hideArrows: false,
    hideDots: false,
    hideDotsMobile: false,
    hideOnDesktop: false,
    hideOnMobile: false,
    fullWidth: false,
    fullWidthMobile: false,
    marginTop: 32,
    marginBottom: 32,
} as const;

/** Most items per view each breakpoint allows (mobile < md ≤ tablet < lg ≤ desktop). */
const MAX_ITEMS = { mobile: 3, tablet: 6, desktop: 7 } as const;

/**
 * Item width per breakpoint and visible count. `half` adds half an item (e.g. 6.5) so the next one
 * peeks in. Each item includes its 16px gutter, so the basis divides the full track. Literal class
 * names so Tailwind can see them.
 */
const BASIS_CLASS = {
    mobile: {
        full: ['basis-full', 'basis-1/2', 'basis-1/3'],
        half: ['basis-[calc(100%/1.5)]', 'basis-[calc(100%/2.5)]', 'basis-[calc(100%/3.5)]'],
    },
    tablet: {
        full: ['md:basis-full', 'md:basis-1/2', 'md:basis-1/3', 'md:basis-1/4', 'md:basis-1/5', 'md:basis-1/6'],
        half: [
            'md:basis-[calc(100%/1.5)]',
            'md:basis-[calc(100%/2.5)]',
            'md:basis-[calc(100%/3.5)]',
            'md:basis-[calc(100%/4.5)]',
            'md:basis-[calc(100%/5.5)]',
            'md:basis-[calc(100%/6.5)]',
        ],
    },
    desktop: {
        full: [
            'lg:basis-full',
            'lg:basis-1/2',
            'lg:basis-1/3',
            'lg:basis-1/4',
            'lg:basis-1/5',
            'lg:basis-1/6',
            'lg:basis-1/7',
        ],
        half: [
            'lg:basis-[calc(100%/1.5)]',
            'lg:basis-[calc(100%/2.5)]',
            'lg:basis-[calc(100%/3.5)]',
            'lg:basis-[calc(100%/4.5)]',
            'lg:basis-[calc(100%/5.5)]',
            'lg:basis-[calc(100%/6.5)]',
            'lg:basis-[calc(100%/7.5)]',
        ],
    },
} as const;

/**
 * 16px gutter between items, dropped when exactly one item fills the view (banners sit edge to
 * edge while sliding). The track's negative margin offsets the first item's gutter.
 */
const GUTTER_CLASS = {
    mobile: { item: ['pl-0', 'pl-4'], track: ['ml-0', '-ml-4'] },
    tablet: { item: ['md:pl-0', 'md:pl-4'], track: ['md:ml-0', 'md:-ml-4'] },
    desktop: { item: ['lg:pl-0', 'lg:pl-4'], track: ['lg:ml-0', 'lg:-ml-4'] },
} as const;

type Breakpoint = keyof typeof MAX_ITEMS;
const BREAKPOINTS: Breakpoint[] = ['mobile', 'tablet', 'desktop'];

/** PD does not validate ranges either: clamp authored margins to something sane. */
const MAX_MARGIN = 200;

/** Empty state in Page Designer: reserves some height so the editor doesn't jump when an item is added. */
const EMPTY_HEIGHT_CLASS = 'h-[250px]';

@Component('carousel', {
    name: 'Carousel',
    description:
        'Generic carousel with an optional header. Choose how many items are visible on mobile, tablet and desktop, optionally with half of the next one peeking in.',
    group: 'priceshoes_layout',
})
@RegionDefinition([
    {
        id: 'items',
        name: 'Items',
        description: 'Add any Content or priceshoes_content component. Each one is an item of the carousel.',
        maxComponents: 20,
        // Every type of the Content and priceshoes_content groups (PD filters by type, not group).
        // Literal list: the cartridge generator reads decorators statically. Add new content types here.
        componentTypeInclusions: [
            'Content.announcementBanner',
            'Content.contentCard',
            'Content.hero',
            'Content.pdImage',
            'Content.popularCategory',
            'Content.productRecommendations',
            'Content.productTile',
            'priceshoes_content.announcementMessage',
            'priceshoes_content.banner',
            'priceshoes_content.catalogCard',
            'priceshoes_content.ctaCard',
            'priceshoes_content.imageCard',
            'priceshoes_content.navColumnLink',
            'priceshoes_content.navLink',
            'priceshoes_content.navService',
            'priceshoes_content.splitHero',
        ],
    },
])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class CarouselMetadata {
    @AttributeDefinition({
        id: 'headerTitle',
        name: 'Header Title',
        description: 'Optional section title, e.g. "Catálogos digitales"',
        type: 'string',
        required: false,
    })
    headerTitle?: string;

    @AttributeDefinition({
        id: 'headerSubtitle',
        name: 'Header Subtitle',
        description: 'Short text next to the header title, e.g. "Solo portadas · toca para abrir"',
        type: 'string',
        required: false,
    })
    headerSubtitle?: string;

    @AttributeDefinition({
        id: 'headerLinkText',
        name: 'Header Link Text',
        description: 'e.g. "Ver más". Shown only when both text and link are set.',
        type: 'string',
        required: false,
    })
    headerLinkText?: string;

    @AttributeDefinition({ id: 'headerLinkUrl', name: 'Header Link', type: 'url', required: false })
    headerLinkUrl?: string;

    @AttributeDefinition({
        id: 'hideHeaderTitleOnMobile',
        name: 'Hide Header Title on Mobile',
        description: 'Hide the header title on mobile; it stays visible on tablet and desktop.',
        type: 'boolean',
        defaultValue: false,
    })
    hideHeaderTitleOnMobile?: boolean;

    @AttributeDefinition({
        id: 'hideHeaderSubtitleOnMobile',
        name: 'Hide Header Subtitle on Mobile',
        description: 'Hide the header subtitle on mobile; it stays visible on tablet and desktop.',
        type: 'boolean',
        defaultValue: false,
    })
    hideHeaderSubtitleOnMobile?: boolean;

    @AttributeDefinition({
        id: 'hideHeaderLinkOnMobile',
        name: 'Hide Header Link on Mobile',
        description: 'Hide the "Ver más" link on mobile; it stays visible on tablet and desktop.',
        type: 'boolean',
        defaultValue: false,
    })
    hideHeaderLinkOnMobile?: boolean;

    @AttributeDefinition({
        id: 'itemsMobile',
        name: 'Items Visible (mobile)',
        description: 'How many items are visible on mobile. Default: 1.',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically. Keep in sync with MAX_ITEMS.
        values: ['1', '2', '3'],
        defaultValue: '1',
    })
    itemsMobile?: string;

    @AttributeDefinition({
        id: 'itemsTablet',
        name: 'Items Visible (tablet)',
        description: 'How many items are visible on tablet. Default: 1.',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically. Keep in sync with MAX_ITEMS.
        values: ['1', '2', '3', '4', '5', '6'],
        defaultValue: '1',
    })
    itemsTablet?: string;

    @AttributeDefinition({
        id: 'itemsDesktop',
        name: 'Items Visible (desktop)',
        description: 'How many items are visible on desktop. Default: 1.',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically. Keep in sync with MAX_ITEMS.
        values: ['1', '2', '3', '4', '5', '6', '7'],
        defaultValue: '1',
    })
    itemsDesktop?: string;

    @AttributeDefinition({
        id: 'showHalfItem',
        name: 'Show Half of the Next Item',
        description:
            'Adds half an item to the visible count on every breakpoint (e.g. 6 becomes 6.5) so shoppers see there is more to scroll.',
        type: 'boolean',
        defaultValue: carouselDefaults.showHalfItem,
    })
    showHalfItem?: boolean;

    @AttributeDefinition({
        id: 'showHalfItemMobile',
        name: 'Show Half of the Next Item (mobile only)',
        description:
            'Adds half an item on mobile only (e.g. 1 becomes 1.5); tablet and desktop keep their exact count. Ignored when "Show Half of the Next Item" is checked.',
        type: 'boolean',
        defaultValue: carouselDefaults.showHalfItemMobile,
    })
    showHalfItemMobile?: boolean;

    @AttributeDefinition({
        id: 'disableAutoPlay',
        name: 'Disable Auto Play',
        description: 'By default the carousel moves automatically. Check to move only with arrows, dots or swipe.',
        type: 'boolean',
        defaultValue: carouselDefaults.disableAutoPlay,
    })
    disableAutoPlay?: boolean;

    @AttributeDefinition({
        id: 'autoPlayInterval',
        name: 'Rotation Interval (ms)',
        description: 'Time between automatic moves; each move advances one item. Default: 5000 (5 seconds).',
        type: 'integer',
        defaultValue: carouselDefaults.autoPlayInterval,
    })
    autoPlayInterval?: number;

    @AttributeDefinition({
        id: 'disableLoop',
        name: 'Disable Infinite Scroll',
        description:
            'By default the carousel wraps around: after the last item comes the first again. Check to stop at the last item (auto play stops there too).',
        type: 'boolean',
        defaultValue: carouselDefaults.disableLoop,
    })
    disableLoop?: boolean;

    @AttributeDefinition({
        id: 'hideArrows',
        name: 'Hide Arrows (desktop)',
        description: 'Hide prev/next arrows on desktop. Arrows are never shown on mobile or when nothing can scroll.',
        type: 'boolean',
        defaultValue: carouselDefaults.hideArrows,
    })
    hideArrows?: boolean;

    @AttributeDefinition({
        id: 'hideDots',
        name: 'Hide Dots',
        description: 'Hide the position dots shown over the bottom of the items.',
        type: 'boolean',
        defaultValue: carouselDefaults.hideDots,
    })
    hideDots?: boolean;

    @AttributeDefinition({
        id: 'hideDotsMobile',
        name: 'Hide Dots (mobile only)',
        description: 'Hide the position dots on mobile only; they stay visible on tablet and desktop.',
        type: 'boolean',
        defaultValue: carouselDefaults.hideDotsMobile,
    })
    hideDotsMobile?: boolean;

    @AttributeDefinition({
        id: 'dotsPosition',
        name: 'Dots Position',
        description:
            'over: white dots over the bottom of the items (banners). below: accent-colored dots under the carousel (cards).',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically.
        values: ['over', 'below'],
        defaultValue: carouselDefaults.dotsPosition,
    })
    dotsPosition?: string;

    @AttributeDefinition({
        id: 'hideOnDesktop',
        name: 'Hide on Desktop',
        description: 'Hide the carousel on desktop (still visible in Page Designer)',
        type: 'boolean',
        defaultValue: carouselDefaults.hideOnDesktop,
    })
    hideOnDesktop?: boolean;

    @AttributeDefinition({
        id: 'hideOnMobile',
        name: 'Hide on Mobile',
        description: 'Hide the carousel on mobile (still visible in Page Designer)',
        type: 'boolean',
        defaultValue: carouselDefaults.hideOnMobile,
    })
    hideOnMobile?: boolean;

    @AttributeDefinition({
        id: 'fullWidth',
        name: 'Full Width',
        description:
            'Stretch the carousel edge to edge (no side margins, and hero banners lose their rounded corners; cards keep them). Unchecked keeps it inside the page container.',
        type: 'boolean',
        defaultValue: carouselDefaults.fullWidth,
    })
    fullWidth?: boolean;

    @AttributeDefinition({
        id: 'fullWidthMobile',
        name: 'Full Width (mobile only)',
        description:
            'Stretch the carousel edge to edge on mobile only (hero banners lose their rounded corners there; cards keep them); tablet and desktop stay inside the page container. Ignored when "Full Width" is checked.',
        type: 'boolean',
        defaultValue: carouselDefaults.fullWidthMobile,
    })
    fullWidthMobile?: boolean;

    @AttributeDefinition({
        id: 'marginTop',
        name: 'Margin Top (px)',
        description: 'Space above the carousel. Default: 32. Range: 0 to 200.',
        type: 'integer',
        defaultValue: carouselDefaults.marginTop,
    })
    marginTop?: number;

    @AttributeDefinition({
        id: 'marginBottom',
        name: 'Margin Bottom (px)',
        description: 'Space below the carousel. Default: 32. Range: 0 to 200.',
        type: 'integer',
        defaultValue: carouselDefaults.marginBottom,
    })
    marginBottom?: number;
}

interface CarouselProps {
    headerTitle?: string;
    headerSubtitle?: string;
    headerLinkText?: string;
    headerLinkUrl?: string;
    hideHeaderTitleOnMobile?: boolean;
    hideHeaderSubtitleOnMobile?: boolean;
    hideHeaderLinkOnMobile?: boolean;
    /** PD enums arrive as strings ("1".."7"). */
    itemsMobile?: string | number;
    itemsTablet?: string | number;
    itemsDesktop?: string | number;
    showHalfItem?: boolean;
    showHalfItemMobile?: boolean;
    disableAutoPlay?: boolean;
    autoPlayInterval?: number;
    disableLoop?: boolean;
    hideArrows?: boolean;
    hideDots?: boolean;
    hideDotsMobile?: boolean;
    /** 'over' (default) or 'below'. */
    dotsPosition?: string;
    hideOnDesktop?: boolean;
    hideOnMobile?: boolean;
    fullWidth?: boolean;
    fullWidthMobile?: boolean;
    marginTop?: number;
    marginBottom?: number;
    /**
     * Set by a parent layout that owns the spacing (e.g. Split Layout): drops the side padding and
     * the authored margins so the carousel fills its column. Not a Page Designer attribute.
     */
    embedded?: boolean;
    /** Plain item list (Storybook/test path). Ignored when the PD `items` region has components. */
    slides?: RegionItem[];
    /** Component data containing regions from Page Designer */
    component?: ComponentType;
    className?: string;
}

function clampMargin(value: number | undefined, fallback: number): number {
    const margin = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    return Math.min(Math.max(margin, 0), MAX_MARGIN);
}

/** Authored count, or the default when it is not a whole number within the breakpoint's range. */
function parseItemCount(value: string | number | undefined, breakpoint: Breakpoint, fallback: number): number {
    const count = Number(value);
    return Number.isInteger(count) && count >= 1 && count <= MAX_ITEMS[breakpoint] ? count : fallback;
}

/** Only the first item is the LCP candidate; components that don't take these props ignore them. */
function withItemProps(comp: ComponentType, index: number): ComponentType {
    const isFirst = index === 0;
    return withComponentData(comp, { priority: isFirst ? 'high' : 'auto', loading: isFirst ? 'eager' : 'lazy' });
}

function usePrefersReducedMotion(): boolean {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }, []);
    return reduced;
}

export default function Carousel({
    headerTitle,
    headerSubtitle,
    headerLinkText,
    headerLinkUrl,
    hideHeaderTitleOnMobile = false,
    hideHeaderSubtitleOnMobile = false,
    hideHeaderLinkOnMobile = false,
    itemsMobile,
    itemsTablet,
    itemsDesktop,
    showHalfItem = carouselDefaults.showHalfItem,
    showHalfItemMobile = carouselDefaults.showHalfItemMobile,
    disableAutoPlay = carouselDefaults.disableAutoPlay,
    autoPlayInterval = carouselDefaults.autoPlayInterval,
    disableLoop = carouselDefaults.disableLoop,
    hideArrows = carouselDefaults.hideArrows,
    hideDots = carouselDefaults.hideDots,
    hideDotsMobile = carouselDefaults.hideDotsMobile,
    dotsPosition = carouselDefaults.dotsPosition,
    hideOnDesktop = carouselDefaults.hideOnDesktop,
    hideOnMobile = carouselDefaults.hideOnMobile,
    fullWidth = carouselDefaults.fullWidth,
    fullWidthMobile = carouselDefaults.fullWidthMobile,
    marginTop,
    marginBottom,
    embedded = false,
    slides: propSlides = NO_REGION_ITEMS,
    component,
    className,
}: CarouselProps): ReactElement | null {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();

    const { items: slides, wrap } = useRegionItems({
        component,
        regionId: 'items',
        fallback: propSlides,
        decorate: withItemProps,
    });

    const counts: Record<Breakpoint, number> = {
        mobile: parseItemCount(itemsMobile, 'mobile', carouselDefaults.itemsMobile),
        tablet: parseItemCount(itemsTablet, 'tablet', carouselDefaults.itemsTablet),
        desktop: parseItemCount(itemsDesktop, 'desktop', carouselDefaults.itemsDesktop),
    };

    const slideCount = slides.length;
    const [api, setApi] = useState<CarouselApi | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    // Scroll positions depend on how many items fit, which changes per breakpoint; Embla recomputes
    // them on resize (reInit). Until it mounts, assume one position per item.
    const [snapCount, setSnapCount] = useState<number | null>(null);
    // Items currently on screen; the rest are hidden from assistive tech and focus. Unknown until
    // Embla measures, so nothing is hidden on the server render.
    const [inView, setInView] = useState<number[] | null>(null);
    const [isPaused, setIsPaused] = useState(false);
    const prefersReducedMotion = usePrefersReducedMotion();
    // Use the authored time as-is; only empty, zero or negative values fall back to the default.
    const interval = autoPlayInterval && autoPlayInterval > 0 ? autoPlayInterval : carouselDefaults.autoPlayInterval;

    useEffect(() => {
        if (!api) return;
        const onSelect = () => setActiveIndex(api.selectedScrollSnap());
        const onReInit = () => {
            setSnapCount(api.scrollSnapList().length);
            onSelect();
        };
        const onSlidesInView = () => setInView(api.slidesInView());
        onReInit();
        onSlidesInView();
        api.on('select', onSelect);
        api.on('reInit', onReInit);
        api.on('slidesInView', onSlidesInView);
        return () => {
            api.off('select', onSelect);
            api.off('reInit', onReInit);
            api.off('slidesInView', onSlidesInView);
        };
    }, [api]);

    const positions = snapCount ?? slideCount;
    const canMove = positions > 1;

    // Re-arming on every index change restarts the countdown after an arrow/dot/swipe. Each move
    // advances one item; when the last one is already visible it goes back to the first,
    // unless infinite scroll is disabled: then it stops at the last one.
    // Paused in Page Designer so the merchant can edit what they are looking at.
    useEffect(() => {
        if (!api || disableAutoPlay || !canMove || isPaused || prefersReducedMotion || isDesignMode) return;
        const timer = setTimeout(() => {
            if (api.canScrollNext()) api.scrollNext();
            else if (!disableLoop) api.scrollTo(0);
        }, interval);
        return () => clearTimeout(timer);
    }, [
        api,
        activeIndex,
        disableAutoPlay,
        disableLoop,
        canMove,
        isPaused,
        prefersReducedMotion,
        isDesignMode,
        interval,
    ]);

    const scrollTo = useCallback((index: number) => api?.scrollTo(index), [api]);

    // In design mode the carousel stays visible on both breakpoints so it can always be edited.
    const visibilityClass = cn(
        !isDesignMode && hideOnMobile && 'hidden',
        !isDesignMode && (hideOnDesktop ? 'md:hidden' : 'md:block')
    );
    if (!isDesignMode && hideOnDesktop && hideOnMobile) return null;

    // Contained (default): always 16px side margins on mobile and 32px from md up (Figma), at any
    // screen width. full width is edge to edge, and hero banners drop their rounded corners; "mobile only"
    // does that below md and keeps the container from md up. Embedded, the parent layout already
    // pads the section, so the carousel just fills its column.
    const containerClass = embedded
        ? 'w-full'
        : fullWidth
          ? 'w-full [&_[data-slot=banner]:not([data-style=card])]:rounded-none'
          : fullWidthMobile
            ? 'w-full md:px-8 max-md:[&_[data-slot=banner]:not([data-style=card])]:rounded-none'
            : 'w-full px-4 md:px-8';

    const style = embedded
        ? undefined
        : {
              marginTop: clampMargin(marginTop, carouselDefaults.marginTop),
              marginBottom: clampMargin(marginBottom, carouselDefaults.marginBottom),
          };

    const header = (
        <SectionHeader
            title={headerTitle}
            subtitle={headerSubtitle}
            linkText={headerLinkText}
            linkUrl={headerLinkUrl}
            hideOnMobile={{
                title: hideHeaderTitleOnMobile,
                subtitle: hideHeaderSubtitleOnMobile,
                link: hideHeaderLinkOnMobile,
            }}
            // Edge to edge on mobile only applies to the items: the header keeps the page margin.
            className={cn('mb-4 md:mb-6', !embedded && !fullWidth && fullWidthMobile && 'px-4 md:px-0')}
        />
    );

    if (slideCount === 0) {
        // Authoring affordance only: on the live storefront an empty carousel renders nothing.
        if (!isDesignMode) return null;
        return (
            <div data-slot="carousel-empty" style={style} className={cn(containerClass, className)}>
                {header}
                {wrap(
                    <div
                        className={cn(
                            'flex items-center justify-center rounded-2xl bg-ps-gray-100 text-ps-gray-600',
                            EMPTY_HEIGHT_CLASS
                        )}>
                        <p>{t('contentCarousel.emptyLabel')}</p>
                    </div>
                )}
            </div>
        );
    }

    // Half item on every breakpoint, or only on mobile (e.g. Campañas: 3 on desktop, 1.5 on mobile).
    const hasHalf = (breakpoint: Breakpoint) => showHalfItem || (showHalfItemMobile && breakpoint === 'mobile');
    const hasGutter = (breakpoint: Breakpoint) => (counts[breakpoint] > 1 || hasHalf(breakpoint) ? 1 : 0);
    const itemClass = cn(
        BREAKPOINTS.flatMap((bp) => [
            BASIS_CLASS[bp][hasHalf(bp) ? 'half' : 'full'][counts[bp] - 1],
            GUTTER_CLASS[bp].item[hasGutter(bp)],
        ])
    );
    const trackClass = cn(BREAKPOINTS.map((bp) => GUTTER_CLASS[bp].track[hasGutter(bp)]));

    // Dots over the items (white, for banners) or below the carousel (accent, for cards like Campañas).
    const showDots = canMove && !hideDots;
    const dotsBelow = dotsPosition === 'below';
    const dots = (
        <div
            data-slot="carousel-dots"
            className={cn(
                'flex justify-center',
                hideDotsMobile && 'max-md:hidden',
                dotsBelow ? 'mt-4 md:mt-6' : 'absolute inset-x-0 bottom-1 md:bottom-5'
            )}>
            {Array.from({ length: positions }, (_, index) => {
                const isActive = index === activeIndex;
                return (
                    <button
                        key={`dot-${index}`}
                        type="button"
                        onClick={() => scrollTo(index)}
                        aria-label={t('contentCarousel.goTo', { current: index + 1, total: positions })}
                        aria-current={isActive ? 'true' : undefined}
                        // 24px hit area (WCAG 2.5.8) around an 8px pip.
                        className={cn(
                            'group flex size-6 cursor-pointer items-center justify-center focus-visible:outline-2',
                            dotsBelow ? 'focus-visible:outline-ps-focus' : 'focus-visible:outline-ps-white'
                        )}>
                        <span
                            aria-hidden="true"
                            className={cn(
                                'block h-2 rounded-full transition-all duration-300',
                                dotsBelow
                                    ? isActive
                                        ? 'w-[18px] bg-ps-accent'
                                        : 'w-2 bg-ps-gray-300 group-hover:bg-ps-gray-400'
                                    : isActive
                                      ? 'w-5 bg-ps-white'
                                      : 'w-2 bg-ps-white/50 group-hover:bg-ps-white/75'
                            )}
                        />
                    </button>
                );
            })}
        </div>
    );

    return (
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- hover/focus handlers only pause auto-rotation; arrows, dots and swipe are the interactive controls
        <section
            data-slot="carousel"
            aria-roledescription="carousel"
            aria-label={headerTitle?.trim() || t('contentCarousel.label')}
            style={style}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            className={cn(containerClass, visibilityClass, className)}>
            {header}
            <div className="relative">
                {wrap(
                    <EmblaCarousel
                        setApi={setApi}
                        opts={{ align: 'start', loop: !disableLoop && slideCount > 1, watchDrag: slideCount > 1 }}>
                        <CarouselContent className={trackClass}>
                            {slides.map((slide, index) => {
                                const isHidden = inView ? !inView.includes(index) : false;
                                return (
                                    <CarouselItem
                                        key={slide.key}
                                        data-slot="carousel-slide"
                                        aria-label={t('contentCarousel.slideLabel', {
                                            current: index + 1,
                                            total: slideCount,
                                        })}
                                        aria-hidden={isHidden ? true : undefined}
                                        inert={isHidden ? true : undefined}
                                        className={itemClass}>
                                        {slide.content}
                                    </CarouselItem>
                                );
                            })}
                        </CarouselContent>
                    </EmblaCarousel>
                )}

                {/* Arrows: desktop only and only when there is something to scroll to. */}
                {canMove && !hideArrows && (
                    <>
                        <ArrowButton
                            direction="previous"
                            label={t('contentCarousel.previous')}
                            onClick={() => api?.scrollPrev()}
                        />
                        <ArrowButton
                            direction="next"
                            label={t('contentCarousel.next')}
                            onClick={() => api?.scrollNext()}
                        />
                    </>
                )}

                {showDots && !dotsBelow && dots}
            </div>
            {showDots && dotsBelow && dots}
        </section>
    );
}

function ArrowButton({
    direction,
    label,
    onClick,
}: {
    direction: 'previous' | 'next';
    label: string;
    onClick: () => void;
}): ReactElement {
    const Icon = direction === 'previous' ? ChevronLeft : ChevronRight;
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className={cn(
                'absolute top-1/2 hidden size-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-ps-white/60 text-ps-black shadow-md backdrop-blur-sm transition-colors hover:bg-ps-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ps-white md:flex',
                direction === 'previous' ? 'left-4' : 'right-4'
            )}>
            <Icon className="size-5" aria-hidden="true" />
        </button>
    );
}
